import { useCallback, useEffect, useRef, useState } from 'react';
import { Camera, CheckCircle2, ChevronRight, Loader2, Plus } from 'lucide-react';
import { api, ErrorApi } from '../api';
import { useEscanerTeclado } from '../lib/useEscanerTeclado';
import { pitidoOk, pitidoError } from '../lib/pitido';
import { useCapa } from '../ui/navegacionReact';
import Ayuda from '../ui/Ayuda';
import CamaraContinua from './CamaraContinua';
import {
  ESTADOS, confirmablesEnBloque, conRespuesta, leer, marcar, resumen, seAbreUnoPorUno,
} from './bandeja';

/* =====================================================================
   ESCANEAR VARIOS · DIRECTRIZ-ALTA-POR-ESCANEO F7
   =====================================================================
   La cámara se queda abierta (o el lector de mano teclea): cada lectura
   suena, vibra y cae en la bandeja. Los que ya son productos de la empresa
   se confirman EN BLOQUE con «Sumar los listos»; lo nuevo se abre uno por
   uno en la Ficha previa (`abrirFicha`). Nada se guarda sin que alguien lo
   confirme: la bandeja solo consulta.
   ===================================================================== */
export default function EscanearVarios({ bandeja, setBandeja, abrirFicha, onSalir, Camara = CamaraContinua }) {
  const [camara, setCamara] = useState(false);
  const [ubicacion, setUbicacion] = useState('');
  const [ubicaciones, setUbicaciones] = useState([]);
  const [sumando, setSumando] = useState(false);
  const [mensaje, setMensaje] = useState(null);
  const pedidos = useRef(new Set());
  useCapa(camara, () => setCamara(false));

  useEffect(() => {
    api.get('/ubicaciones').then((u) => setUbicaciones(Array.isArray(u) ? u : [])).catch(() => setUbicaciones([]));
  }, []);

  // Cada código nuevo se consulta una vez (la ficha completa: así abrirla
  // después no vuelve a esperar).
  useEffect(() => {
    for (const item of bandeja) {
      if (item.estado !== 'consultando' || pedidos.current.has(item.codigo)) continue;
      pedidos.current.add(item.codigo);
      api.get(`/alta-escaneo/ficha?codigo=${encodeURIComponent(item.codigo)}`)
        .then((r) => setBandeja((b) => conRespuesta(b, item.codigo, r)))
        .catch(() => setBandeja((b) => conRespuesta(b, item.codigo, null)));
    }
  }, [bandeja, setBandeja]);

  // LA BANDEJA VIGENTE, POR REFERENCIA. Dos lecturas pueden llegar antes de
  // que React vuelva a pintar; si cada una partiera de la bandeja del último
  // render, la segunda no vería a la primera y la contaría como nueva.
  const vigente = useRef(bandeja);
  useEffect(() => { vigente.current = bandeja; }, [bandeja]);

  const alLeer = useCallback((codigo) => {
    const r = leer(vigente.current, codigo, Date.now());
    vigente.current = r.bandeja;
    setBandeja(r.bandeja);
    if (r.que === 'repetida') return;
    pitidoOk();
    try { navigator.vibrate?.(60); } catch { /* sin vibración en este aparato */ }
  }, [setBandeja]);

  useEscanerTeclado((leido) => alLeer(leido), { activo: !camara });

  const listos = confirmablesEnBloque(bandeja);
  const falta = !listos.length ? null : (!ubicacion ? 'Elige en «Ubicación» dónde entra la mercancía.' : null);

  async function sumarListos() {
    setSumando(true);
    setMensaje(null);
    let bien = 0;
    const fallas = [];
    for (const item of listos) {
      try {
        await api.post('/alta-escaneo/sumar', { codigo: item.codigo, cantidad: String(item.cantidad), ubicacion,
          medio: 'CAMARA' });
        bien += 1;
        setBandeja((b) => marcar(b, item.codigo, 'hecho'));
      } catch (e) {
        fallas.push(`${item.codigo}: ${e instanceof ErrorApi ? e.message : 'sin conexión con el servidor'}`);
      }
    }
    setSumando(false);
    if (fallas.length) pitidoError(); else pitidoOk();
    setMensaje({ bien, fallas });
  }

  return (
    <div className="space-y-4 pb-24" data-alta-escaneo="varios">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Escanear varios</h2>
        <p className="text-sm text-gray-500 mt-0.5">Escanea uno tras otro. Revisa la bandeja y confirma.</p>
      </div>
      <Ayuda id="alta_escaneo.varios.pantalla" />
      {camara ? (
        <Camara onCodigo={alLeer} onCerrar={() => setCamara(false)} />
      ) : (
        <button type="button" onClick={() => setCamara(true)}
          className="w-full min-h-14 inline-flex items-center justify-center gap-2 bg-[var(--color-marca)] text-white font-semibold rounded-xl px-4">
          <Camera className="h-5 w-5" aria-hidden="true" /> Abrir la cámara
        </button>
      )}
      <Ayuda id="alta_escaneo.varios.repetidas" />

      <div className="rounded-xl border border-gray-200" data-alta-escaneo="bandeja">
        <p className="px-3 py-2 text-sm font-semibold text-gray-700 border-b border-gray-100" data-alta-escaneo="resumen">
          {bandeja.length ? resumen(bandeja) : 'La bandeja está vacía. Escanea el primer producto.'}
        </p>
        <ul>
          {bandeja.map((item) => (
            <li key={item.codigo} className="border-b border-gray-100 last:border-0">
              <button type="button" disabled={!seAbreUnoPorUno(item)}
                onClick={() => abrirFicha(item)}
                className="w-full min-h-12 flex items-center justify-between gap-2 px-3 py-2 text-left disabled:cursor-default"
                data-codigo={item.codigo} data-estado={item.estado}>
                <span className="min-w-0">
                  <span className="block text-sm text-gray-900 truncate">
                    {item.respuesta?.ya_es_de?.nombre || item.respuesta?.ficha?.campos?.nombre || item.codigo}
                  </span>
                  <span className="block text-xs text-gray-500">
                    <span className="font-mono">{item.codigo}</span> · {item.cantidad} {item.cantidad === 1 ? 'pieza' : 'piezas'}
                  </span>
                </span>
                <span className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-gray-700">
                  {item.estado === 'consultando' && <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />}
                  {item.estado === 'hecho' && <CheckCircle2 className="h-3 w-3 text-green-700" aria-hidden="true" />}
                  {ESTADOS[item.estado]}
                  {seAbreUnoPorUno(item) && <ChevronRight className="h-4 w-4" aria-hidden="true" />}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>

      {listos.length > 0 && (
        <div className="space-y-2" data-alta-escaneo="en-bloque">
          <label htmlFor="ae-varios-ubicacion" className="block text-sm font-semibold text-gray-700">Ubicación</label>
          <select id="ae-varios-ubicacion" className="entrada" value={ubicacion} onChange={(e) => setUbicacion(e.target.value)}>
            <option value="">Elige dónde entra</option>
            {ubicaciones.map((u) => <option key={u.codigo} value={u.codigo}>{u.codigo} — {u.nombre}</option>)}
          </select>
          <button type="button" onClick={sumarListos} disabled={Boolean(falta) || sumando}
            className="w-full min-h-12 inline-flex items-center justify-center gap-2 bg-[var(--color-marca)] disabled:opacity-60 text-white font-semibold rounded-xl px-4">
            {sumando ? <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> : <Plus className="h-5 w-5" aria-hidden="true" />}
            {`Sumar los ${listos.length} listos`}
          </button>
          {falta && <p className="text-xs text-gray-600">{falta}</p>}
          <Ayuda id="alta_escaneo.varios.en_bloque" />
        </div>
      )}
      {mensaje && (
        <div role="status" className="text-sm space-y-1" data-alta-escaneo="sumados">
          {mensaje.bien > 0 && <p className="text-green-800">{`Listo: se sumó la existencia de ${mensaje.bien} ${mensaje.bien === 1 ? 'producto' : 'productos'}.`}</p>}
          {mensaje.fallas.map((f) => <p key={f} className="text-red-700">{f}</p>)}
        </div>
      )}
      <button type="button" onClick={onSalir}
        className="w-full min-h-11 inline-flex items-center justify-center gap-2 bg-white border border-gray-300 text-gray-800 font-semibold rounded-xl px-4">
        Terminar
      </button>
    </div>
  );
}
